import React, {
  Fragment,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { MENU_SLUG } from "../../constants/constants";
import { useNavigate } from "react-router-dom";
import PageHead from "../../components/pageHead";
import Button from "../../components/button";
import AddIcon from "../../icons/addIcon";
import CarCard from "../../components/carCard";
import AddCarModal from "../../components/addCarModal";
import DeleteCarModal from "../../components/deleteModal";
import IconButton from "../../components/iconButton";
import useMediaQuery from "../../hooks/useMediaQuery";
import FileDownloadIcon from "../../icons/fileDownloadIcon";
import MobileNav from "../../components/mobileNav";
import DBQuickStartGuide from "../../assets/files/DBQuickStartGuide.pdf";
import { Popover } from "react-tiny-popover";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import { useAuth } from "../../hooks/useAuth";
import { useUpdateCurrentCar } from "../../hooks/updateCurrentCar";
import { CarContext } from "../../contexts/carProvider";
import Tour from "reactour";
import styles from "./style.module.css";

const MyCars = () => {
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const updateCurrentCar = useUpdateCurrentCar();

  const isMobile = useMediaQuery("(max-width: 768px)");
  const [openAddCarModal, setOpenAddCarModal] = useState(false);
  const [openDeleteCarModal, setOpenDeleteCarModal] = useState(false);
  const { cars, loading } = useContext(CarContext);
  const [activeCar, setActiveCar] = useState();
  const [hasCars, setHasCars] = useState(false);
  const [modalTitle, setModalTitle] = useState("Add Car");
  const [buttonTitle, setButtonTitle] = useState("Add");
  const [formData, setFormData] = useState({});
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [activeCarStatus, setActiveCarStatus] = useState(false);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const handleCarClick = useCallback(async (car) => {
    try {
      let url = `/cars/set-current`;
      let options = {
        method: "PUT",
        url,
        data: {
          carId: car._id,
        },
      };
      await axiosPrivate(options)
        .then((response) => {
          if (response.data.status === 1) {
            updateCurrentCar(response);
            setActiveCar(car);
            setActiveCarStatus(false);
          }
        })
        .catch((err) => {
          if (err.response) {
          }
        });
    } catch (error) {
      // console.log(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenEditCarModal = (car) => {
    setModalTitle("Edit Car");
    setFormData(car);
    setButtonTitle("Save");
    setOpenAddCarModal(true);
  };

  const handleOpenAddCarModal = () => {
    setIsTourOpen(false);
    setFormData({});
    setModalTitle("Add New Car");
    setButtonTitle("Add");
    setOpenAddCarModal(true);
  };

  const handleCloseAddCarModal = () => {
    setOpenAddCarModal(false);
  };

  const handleOpenDeleteCarModal = (car) => {
    setOpenDeleteCarModal(true);
    setFormData(car);
  };

  const handleCloseDeleteCarModal = () => {
    setOpenDeleteCarModal(false);
  };

  const handleDeleteCar = async () => {
    const response = await axiosPrivate.delete(`/cars/${formData._id}`);
    if (response.data.status === 1) {
      updateCurrentCar(response);
      handleCloseDeleteCarModal();
    }
  };

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const handleContinue = () => {
    navigate(`/${MENU_SLUG.snapshot}`);
  };

  const handleDownloadGuide = () => {
    const link = document.createElement("a");
    link.href = DBQuickStartGuide;
    link.download = "DashBook Quick Start Guide.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCloseTour = () => {
    setIsTourOpen(false);
  };

  const tourSteps = [
    {
      selector: '[data-tour="add-car-button"]',
      content: (
        <>
          <h3>Add New Car</h3>
          <p>Click this button to set up your vehicle in DashBook.</p>
          <ul>
            <li>
              <strong>Car Name:</strong> this can be a description like "2025
              Porsche 911" or a nickname like "Tom's Track Car".
            </li>
          </ul>
          <p>
            <strong>Note:</strong> Currently, DashBook allows only one car per
            user account.
          </p>
        </>
      ),
      position: "bottom",
    },
    {
      selector: '[data-tour="download-guide-button"]',
      content: (
        <>
          <h3>Download Quick Start Guide</h3>
          <p>
            Click this icon to download our comprehensive Quick Start Guide.
            This PDF contains:
          </p>
          <ul>
            <li>Step-by-step setup instructions</li>
            <li>How to configure your consumables</li>
            <li>Tips for tracking maintenance</li>
            <li>Best practices for track day logging</li>
          </ul>
          <p>
            <strong>Pro Tip:</strong> Download to get up to speed fast and enjoy
            the full benefit of DashBook from day one!
          </p>
        </>
      ),
      position: "bottom",
    },
  ];

  useEffect(() => {
    if (cars.length > 0 && auth.currentCar) {
      setActiveCar(cars.find((car) => car._id === auth.currentCar));
    }
  }, [cars, auth.currentCar]);

  useEffect(() => {
    if (auth.multipleCars) {
      setHasCars(true);
    } else {
      if (cars.length === 0) {
        setHasCars(true);
      } else {
        setHasCars(false);
      }
    }
  }, [auth.multipleCars, cars]);

  useEffect(() => {
    if (cars.length > 0 && activeCarStatus) {
      navigate(`/${MENU_SLUG.snapshot}`);
    }
    // eslint-disable-next-line
  }, [cars, activeCarStatus]);

  useEffect(() => {
    if (!loading) {
      if (cars.length === 0) {
        setIsTourOpen(true);
      }
    }
  }, [loading, cars]);

  return (
    <Fragment>
      <PageHead
        title="My Cars"
        logo={true}
        logoLink={`/${MENU_SLUG.my_cars}`}
        carChangeDisabled={true}
      />
      <div className={styles.body}>
        <div className={styles.body_head}>
          <div className={styles.body_head_inner}>
            <div className={styles.filters}></div>
            <div className={styles.add_btn}>
              {isMobile ? (
                <Popover
                  containerClassName={`${styles.popover}`}
                  isOpen={!hasCars && tooltipOpen}
                  positions={["top"]}
                  align="end"
                  content={
                    <div className={styles.tooltipContent}>
                      We currently allow only one car per user.
                    </div>
                  }
                  transformMode="relative"
                >
                  <span className={hasCars ? "" : styles.disabled}>
                    <IconButton
                      customClass={styles.add_icon}
                      icon={<AddIcon />}
                      onClick={hasCars ? handleOpenAddCarModal : null}
                      onMouseEnter={handleTooltipOpen}
                      onMouseLeave={handleTooltipClose}
                      dataTour="add-car-button"
                    />
                  </span>
                </Popover>
              ) : (
                <Popover
                  containerClassName={`${styles.popover}`}
                  isOpen={!hasCars && tooltipOpen}
                  positions={["top"]}
                  align="end"
                  content={
                    <div className={styles.tooltipContent}>
                      We currently allow only one car per user.
                    </div>
                  }
                  transformMode="relative"
                >
                  <span className={hasCars ? "" : styles.disabled}>
                    <Button
                      title={"Add Car"}
                      buttonType="outlined"
                      buttonColor="primary"
                      startIcon={<AddIcon />}
                      onClick={hasCars ? handleOpenAddCarModal : null}
                      onMouseEnter={handleTooltipOpen}
                      onMouseLeave={handleTooltipClose}
                      dataTour="add-car-button"
                    />
                  </span>
                </Popover>
              )}
              <IconButton
                icon={<FileDownloadIcon />}
                isTooltip={true}
                tooltipContent="Download the Quick Start Guide"
                tooltipPosition={["top"]}
                tooltipAlign="end"
                tooltipTransform={{ top: -10, right: 0 }}
                onClick={handleDownloadGuide}
                dataTour="download-guide-button"
              />
            </div>
          </div>
        </div>
        <div className={styles.body_view}>
          <div className={styles.body_view_inner}>
            <div className={styles.car_list}>
              {cars.map((car, index) => (
                <CarCard
                  key={index}
                  car={car}
                  activeCar={activeCar}
                  handleCarClick={handleCarClick}
                  handleOpenEditCarModal={handleOpenEditCarModal}
                  handleOpenDeleteCarModal={handleOpenDeleteCarModal}
                />
              ))}
            </div>
          </div>
          {cars.length > 0 && (
            <div className={styles.car_continue_btn}>
              <Button
                title={"Continue"}
                buttonType="outlined"
                buttonColor="primary"
                onClick={handleContinue}
              />
            </div>
          )}
        </div>
      </div>
      {isMobile && <MobileNav defaultActive={true} isScrolling={false} />}
      <AddCarModal
        open={openAddCarModal}
        onClose={handleCloseAddCarModal}
        title={modalTitle}
        carData={formData}
        buttonTitle={buttonTitle}
      />
      <DeleteCarModal
        open={openDeleteCarModal}
        onClose={handleCloseDeleteCarModal}
        title="Deleting Car"
        description="Are you sure you want to delete this record? This process cannot be undone."
        onDelete={handleDeleteCar}
      />

      <Tour
        steps={tourSteps}
        isOpen={isTourOpen}
        onRequestClose={handleCloseTour}
        showCloseButton={currentStep === tourSteps.length}
        showNavigation={false}
        showNumber={false}
        rounded={8}
        className={styles.tour}
        getCurrentStep={(curr) => setCurrentStep(curr + 1)}
      />
    </Fragment>
  );
};

export default MyCars;
